import React, { FC } from 'react';
import { RootState, AppDispatch } from '../store';
import { useSelector, useDispatch } from 'react-redux';
import {
	Platform,
	Orientation,
	WriteDirection,
	PopOverSceneItemData,
	RangeSettings,
	DeviceType,
	SofaConfiguration,
	ArmrestsByCover,
} from 'sofa-reborn-react';
import PopoverMenu from '../components/PopoverMenu';
import { clearAdditionalCoverItems } from '../store/cover';
import { setCurrentLightbox } from '../store/application/actions';
import { setShowSelectAll } from '../store/actionButtons';
import {
	removeSceneItem,
	rotateSceneItem,
	removeAllSceneItems,
	moveBackrest,
	moveBackrestAsArmrest,
} from '../store/scene/actions';
import { ProductData } from '@ikea-aoa/component-view-3d';
import { ProductUtils } from '../util/ProductUtils';

const onInfoClick = (dispatch: AppDispatch): void => {
	dispatch(setCurrentLightbox('additional-information'));
};

const onClickRemove = (dispatch: AppDispatch, sceneItem: PopOverSceneItemData): void => {
	dispatch(removeSceneItem(sceneItem));
	dispatch(clearAdditionalCoverItems());
};

const onClickRemoveAll = (dispatch: AppDispatch): void => {
	dispatch(removeAllSceneItems(true));
};

const onClickRotate = (dispatch: AppDispatch, sceneItem: PopOverSceneItemData): void => {
	dispatch(rotateSceneItem(sceneItem));
};

const onBackDropClick = (dispatch: AppDispatch): void => {
	dispatch(setShowSelectAll(false));
};

const onArmrestMoveClick = (dispatch: AppDispatch, sceneItem: PopOverSceneItemData): void => {
	dispatch(moveBackrest(sceneItem));
};

const onBackrestAsArmrestClick = (dispatch: AppDispatch, sceneItem: PopOverSceneItemData): void => {
	dispatch(moveBackrestAsArmrest(sceneItem));
};

const getPosition = (
	selectedSceneItem: PopOverSceneItemData,
	sofaConfiguration: SofaConfiguration,
	deviceType: DeviceType,
	orientation: Orientation,
	platform: Platform
): { x: number; y: number } => {
	if (selectedSceneItem) {
		if (deviceType === 'desktop' && selectedSceneItem.screenPosition.y < 190) {
			return { x: selectedSceneItem.screenPosition.x, y: 190 };
		}
		return selectedSceneItem.screenPosition;
	} else if (sofaConfiguration) {
		const sceneElement = document.getElementById('scene');
		if (sceneElement) {
			return {
				x: sceneElement.getBoundingClientRect().left + sceneElement.clientWidth / 2,
				y:
					deviceType === 'mobile' && orientation === 'landscape'
						? sceneElement.clientHeight / 1.52
						: platform === 'kiosk'
						? 280
						: deviceType === 'desktop'
						? 190
						: 166,
			};
		} else {
			throw 'PopoverMenuContainer: getPosition(): sceneElement does not exist';
		}
	}

	return { x: 0, y: 0 };
};

const PopoverMenuContainer: FC = () => {
	const platform = useSelector<RootState, Platform>((state) => state.deviceInfo.platform);
	const deviceType = useSelector<RootState, DeviceType>((state) => state.deviceInfo.deviceType);
	const orientation = useSelector<RootState, Orientation>((state) => state.deviceInfo.orientation);
	const selectedSceneItem = useSelector<RootState, PopOverSceneItemData>(
		(state) => state.scene.selectedSceneItem as PopOverSceneItemData
	);
	const productData = useSelector<RootState, ProductData>((state) => state.products.products);
	const writeDirection = useSelector<RootState, WriteDirection>(
		(state) => state.localization.writeDirection
	);
	const rangeSettings = useSelector<RootState, RangeSettings>(
		(state) => state.settings.rangeSettings as RangeSettings
	);
	const sofaConfiguration = useSelector<RootState, SofaConfiguration>(
		(state) => state.sofaConfiguration.sofaConfiguration
	);
	const armrest = useSelector<RootState, ArmrestsByCover | null>((state) => state.armrest.armrests);
	const selectedItem = selectedSceneItem && productData[selectedSceneItem.articleId];
	const showArmrestButton = armrest && selectedItem.filter.family === 'ARMREST' ? true : false;
	const showBackrestMoveButton =
		selectedSceneItem.disconnectable && selectedItem.filter.family === 'BACKREST';
	const showBackrestAsArmrestButton =
		rangeSettings.rangeName === 'JÄTTEBO' && selectedItem.modelId === 'JATTEBO_1_5_SEAT';
	const disableBackrestMoveButton = useSelector<RootState, boolean>(
		(state) => !!state.scene.disableMoveBackrest
	);
	const disableBackrestAsArmrestMoveButton = useSelector<RootState, boolean>(
		(state) => !!state.scene.disableMoveBackrestAsArmrest
	);
	const coverOnly = useSelector<RootState, boolean | undefined>((state) => state.cover.coverOnly);
	const showInfoButton =
		selectedSceneItem &&
		(ProductUtils.getSprIdValid(productData, selectedSceneItem.articleId) || coverOnly);
	const showRemoveButton =
		(selectedSceneItem && !productData[selectedSceneItem.articleId].filter.noArticleId) ||
		(!selectedSceneItem && sofaConfiguration !== null);
	const showRotateButton = selectedSceneItem && selectedSceneItem.rotatable;

	const dispatch = useDispatch();

	if (
		!showInfoButton &&
		!showRemoveButton &&
		!showRotateButton &&
		!showArmrestButton &&
		!showBackrestMoveButton &&
		!showBackrestAsArmrestButton
	) {
		return null;
	}

	return (
		<PopoverMenu
			platform={platform}
			deviceType={deviceType}
			orientation={orientation}
			writeDirection={writeDirection}
			disconnectable={!selectedSceneItem || selectedSceneItem.disconnectable || false}
			top={getPosition(selectedSceneItem, sofaConfiguration, deviceType, orientation, platform).y}
			left={getPosition(selectedSceneItem, sofaConfiguration, deviceType, orientation, platform).x}
			showRemoveButton={!showBackrestMoveButton}
			showRotateButton={showRotateButton}
			showInfoButton={showInfoButton}
			showColorOptions={!rangeSettings.shouldColorAllModules}
			showArmrestButton={showArmrestButton}
			showBackrestMoveButton={showBackrestMoveButton}
			showBackrestAsArmrestButton={showBackrestAsArmrestButton}
			disableBackrestAsArmrestMoveButton={disableBackrestAsArmrestMoveButton}
			disableBackrestMoveButton={disableBackrestMoveButton}
			onInfoClick={(): void => onInfoClick(dispatch)}
			onClickRemove={
				selectedSceneItem
					? (): void => onClickRemove(dispatch, selectedSceneItem)
					: (): void => onClickRemoveAll(dispatch)
			}
			onClickRotate={(): void => onClickRotate(dispatch, selectedSceneItem)}
			onBackDropClick={(): void => onBackDropClick(dispatch)}
			onBackrestMoveClick={(): void => onArmrestMoveClick(dispatch, selectedSceneItem)}
			onBackrestAsArmrestClick={(): void => onBackrestAsArmrestClick(dispatch, selectedSceneItem)}
		></PopoverMenu>
	);
};

export default PopoverMenuContainer;
